/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import config from '../../../../../share/config';
import session from '../../../../../share/session';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../../share/util/api/polling';
import cstmTable from "../../../../../share/components/vue/PaginationTable";

/** 各フェーズ定義 */
const PHASE = {
  START: 'start', // 開始画面
  WAIT: 'wait', // 待機画面
  SUCCESS: 'success', // 停止画面
  FAILED: 'failed', // エラー画面
};

/** API type定義 */
const API_TYPE = {
  READ: "read",
  STANDBY: "standby",
  WRITE_ON: 'write-on',
  WRITE_OFF: 'write-off',
  TERMINATE: "terminate",
  DISPOSE: "dispose"
};

export default {
  name: 'requirementSpDCM5',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      isDispose: true,
      systemId: '',
      modal_special_requirement_DCM5: false,
      exitPromise: null,
      // テーブル関連
      tableItems: [],
      // 選択
      selectedItem: null,
      // データ読込み結果
      readMode: null,
    };
  },
  components: {
    'cstm-table': cstmTable
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: (state) => state.netAppConnector.pairingAppId
    }),

    isOkDisabled() {
      // WAIT のときだけ「選択されていないとOK押せない」
      if (this.phase === PHASE.WAIT) {
        return !this.selectedItem || this.isLoading;
      }
      // それ以外の phase は従来通り isLoading のみ
      return this.isLoading;
    },

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      // 開始画面
      const startTitle = 'individual.common_title_execution';
      // 待機画面
      const confirmationTitle = 'message.common_title_confirmation';
      // 停止画面
      const successTitle = 'message.common_title_success';
      // エラー画面
      const errorTitle = 'message.common_title_error';
      const titleMap = {
        start: startTitle,
        wait: confirmationTitle,
        success: successTitle,
        failed: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        start: '',
        wait: `${this.i18nWsKey}.REQ_SP_WS_DCM5_TXT_1`,
        success: `${this.i18nWsKey}.REQ_SP_WS_DCM5_TXT_2`,
        failed: `${this.i18nWsKey}.REQ_SP_WS_DCM5_TXT_3`,
      };
      switch (this.phase) {
        case PHASE.WAIT:
        case PHASE.SUCCESS:
          return this.$t(messageMap[this.phase]).replace('$DID$', this.readMode);
        default:
          return this.$t(messageMap[this.phase]);
      }
    },

    showButtonOk() {
      const showOkButtonMap = {
        start: false,
        wait: true,
        success: true,
        failed: true,
      };
      return !!showOkButtonMap[this.phase];
    },

    showButtonCancel() {
      return this.phase === PHASE.WAIT;
    },

    /** 表の情報 */
    systemTableColumns() {
      return [
        {
          name: 'itemName',
          label: 'individual.label_table_item',
          useResource: false,
        }
      ];
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    /**
     * 選択された行データを保持する
     * @param {Object} row 選択されたテーブル行データ
     */
    onSelectItem(row) {
      this.selectedItem = row;
      this.setHighLight();
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLight() {
      this.tableItems.forEach(item => {
        if (item === this.selectedItem) {
          item.fieldSpec = {};
          item.fieldSpec.itemName = config.LIST_ITEM_HIGHLIGHT;
        } else if (item.fieldSpec) {
          delete item.fieldSpec;
        }
      });
    },

    /** 初期化処理 */
    async init() {
      this.modal_special_requirement_DCM5 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // リソース(特殊機能_DID)のi18nキー取得
      this.i18nSpwsDidKey = `spwsDid${this.systemId}`;
      // データ読込み
      await this.read(true);
    },

    /**
     * データ読込み
     * @param {boolean} initFlag 初期化処理フラグ
     */
    async read(initFlag) {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: API_TYPE.READ,
            is_special: true
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED: {
            if (initFlag) {
              // 待機画面へ遷移
              this.phase = PHASE.WAIT;
              this.$nextTick(() => {
                this.createTable();
              });
            } else {
              await this.terminate();
              // 停止画面へ遷移
              this.phase = PHASE.SUCCESS;
            }

            // 表示用DID編集処理
            const readOutData = response.data.user_data.readout_data?.[0];
            if (!readOutData) { await this.showUnexpectedError(); break; }

            const rec = readOutData.data_record;
            if (rec === 'FF' || rec === '00') {
              this.readMode = this.$t(`${this.i18nSpwsDidKey}.${(readOutData.did + '_' + rec).trim()}`);
            } else {
              this.readMode = `0x${rec}`;
            }
            break;
          }
          default:
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** セッション遷移・セキュリティアクセス処理 */
    async standby() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: API_TYPE.STANDBY,
            is_special: true
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        this.isDispose = response.data.user_data?.is_dispose ?? true;

        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // データ書込み処理を実施
            await this.write();
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // エラーへ遷移
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** データ書込み */
    async write() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: this.selectedItem.mode,
            is_special: true
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // データ読出し処理を実施
            await this.read(false);
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // エラーへ遷移
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** terminate処理 */
    async terminate() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: API_TYPE.TERMINATE,
            is_special: true
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, "terminate"); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, "terminate"); },
        );
        if (!response) return;

      } finally {
        this.isLoading = false;
      }
    },

    /** Dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: API_TYPE.DISPOSE,
            is_special: true
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, "dispose"); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, "dispose"); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** テーブルデータ作成 */
    createTable() {
      this.tableItems = [
        { itemName: this.$t(`${this.i18nWsKey}.data_read_DCM5_0xFF`), mode: API_TYPE.WRITE_ON },
        { itemName: this.$t(`${this.i18nWsKey}.data_read_DCM5_0x00`), mode: API_TYPE.WRITE_OFF },
      ];
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} calledFrom 呼び出し元
     */
    validateErrorResult(result, calledFrom = '') {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => {
          result;
          // terminate,disposeから呼ばれた場合はエラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          this.showUnexpectedError();
        },
      );
    },

    /** 終了/dispose処理 */
    async exitProcess() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.isDispose ? this.dispose() : this.terminate();
      await this.exitPromise;
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      await this.exitProcess();
      this.phase = PHASE.FAILED;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_DCM5 = false;
    },

    /** OKボタン押下時の処理 */
    async onOkClicked() {
      switch (this.phase) {
        // 待機画面
        case PHASE.WAIT:
          await this.standby();
          break;
        // 停止画面・エラー画面
        case PHASE.SUCCESS:
        case PHASE.FAILED:
        default:
          this.closeWizard();
          break;
      }
    },

    /** キャンセルボタン押下時の処理 */
    async onCancelClicked() {
      await this.exitProcess();
      this.closeWizard();
    },
  },

  mounted() {
    this.init();
  },
};

